import { debounce } from 'debounce';

import { TextBox } from '@syncfusion/ej2-inputs';

import { routes } from '@/shared/routes';
import { CompaniesHouseSearchListItem, CompaniesHouseSearchResponse } from '@/shared/types/companiesHouse';
import { escapeHtml } from '@/shared/utils/misc';

import { getControl } from '@/front/editable/state';
import { listen } from '@/front/event';

listen('editablesReady', () => {
  [...document.querySelectorAll('[data-companies-house-search]')]
    .map((element) => element as HTMLElement)
    .forEach(initCompaniesHouseSearch);
});

const initCompaniesHouseSearch = (container: HTMLElement) => {
  const editable = container.querySelector('.editable[data-field="registrationNumber"]') as HTMLDivElement | null;
  const results = container.querySelector('[data-companies-house-search-results]') as HTMLElement | null;

  if (!editable || !results) {
    return;
  }

  const textBox = getControl(editable.id) as TextBox | undefined;
  const input = editable.querySelector('input') as HTMLInputElement | null;

  if (!textBox || !input) {
    return;
  }

  let abortController: AbortController | undefined;

  const hideResults = () => {
    results.style.display = 'none';
    results.innerHTML = '';
  };

  const setRegistrationNumber = (companyNumber: string) => {
    textBox.value = companyNumber;
    input.value = companyNumber;

    const form = container.closest('form');
    if (form) {
      form.dataset.dirty = 'true';
    }

    hideResults();
  };

  const renderResults = (items: CompaniesHouseSearchListItem[]) => {
    if (!items.length) {
      hideResults();
      return;
    }

    results.innerHTML = items
      .map(
        (item) => `
          <button
            type="button"
            class="dropdown-item text-start w-100 border-0 bg-transparent px-3 py-2 d-flex gap-1"
            data-company-number="${item.companyNumber}"
          >
            <span class="flex-shrink-0">${item.companyNumber} -</span>
            <span class="text-truncate min-w-0">${escapeHtml(item.companyName)}</span>
          </button>
        `,
      )
      .join('');

    results.style.display = 'block';

    [...results.querySelectorAll('button[data-company-number]')].forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const companyNumber = (button as HTMLButtonElement).dataset.companyNumber;
        if (companyNumber) {
          setRegistrationNumber(companyNumber);
        }
      });
    });
  };

  const search = async (query: string) => {
    abortController?.abort();

    if (query.length < 2) {
      hideResults();
      return;
    }

    abortController = new AbortController();

    try {
      const url = `${routes.organisations.companiesHouseSearch()}?query=${encodeURIComponent(query)}`;
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: abortController.signal,
      });

      if (!response.ok) {
        hideResults();
        return;
      }

      const data = (await response.json()) as CompaniesHouseSearchResponse;
      renderResults(data.items ?? []);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return;
      }
      hideResults();
    }
  };

  const debouncedSearch = debounce((query: string) => {
    search(query);
  }, 300);

  input.addEventListener('input', () => {
    debouncedSearch(input.value.trim());
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      hideResults();
    }
  });

  document.addEventListener('click', (event) => {
    if (!container.contains(event.target as Node)) {
      hideResults();
    }
  });
};
